import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { QRCodeCanvas } from 'qrcode.react';
import { MentiLogo } from './MentiLogo';

export interface QRCodeHandler {
  downloadQrCode: () => void;
}

/**
 * A component that can be used to generate a QR code link. It can also be used to download
 * the QR code using the downloadQrCode() function provided via ref.
 *
 * @component
 * @param {string} url
 * @param {RefObject<QRCodeHandler>} ref
 * @example
 * const ref = useRef<QRCodeHandler>(null);
 * ...
 * ref.current?.downloadQrCode()
 * ...
 * <QRCode url='https://google.com' ref={ref} />
 */
export const QRCode = forwardRef<QRCodeHandler, { url: string }>(
  ({ url }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const t = useTranslations('presenting');

    useImperativeHandle(ref, () => {
      return {
        downloadQrCode() {
          if (!canvasRef.current) {
            throw Error('QR canvas missing');
          }

          const link = document.createElement('a');
          link.download = 'mentimeter_qr_code.png';

          canvasRef.current.toBlob((blob) => {
            if (blob === null) return;

            link.href = URL.createObjectURL(blob);
            link.click();

            // Release memory
            URL.revokeObjectURL(link.href);
          });
        },
      };
    });

    return (
      <QRCodeCanvas
        ref={canvasRef}
        data-testid="join-instructions-qrcode"
        value={url}
        // Large size so it doesn't get blurry when upscaled
        size={1000}
        // But display it at parent containers size
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '10px',
        }}
        level="M"
        aria-label={t('qr_code_a11y')}
        imageSettings={{
          height: 168,
          width: 200,
          excavate: true,
          src: MentiLogo,
        }}
      />
    );
  },
);
